import React, { FC, useCallback, useState, ReactNode } from 'react';
import { ArrowContainer } from 'react-tiny-popover';
import { StaticImage } from 'gatsby-plugin-image';
import { Experience, ExperienceStatus } from '@/webapi/use-experience-api';
import { Chip, CONTROL } from '@/features/details/shared';
import { PopoverContent } from '@/features/details/rebalance-chip/popover-content';
import { ResponsivePopover } from '@/components/responsive-popover';
import { isMobile } from '@/components/responsive';

type Props = {
  experience: Experience;
  setExperience: (e: Experience) => void;
  children?: ReactNode;
  togglePopover?: (expId) => void;
  isOpenFromParent?: boolean;
  whiteBg?: boolean;
};

export const ReBalanceChip: FC<Props> = ({
  experience,
  setExperience,
  children,
  togglePopover,
  isOpenFromParent,
  whiteBg,
}) => {
  const { toggle: localToggle, isOpen: localIsOpen } = useToggle();

  const useParentState =
    togglePopover !== undefined && isOpenFromParent !== undefined;

  const isOpen = useParentState ? isOpenFromParent : localIsOpen;
  const toggle = useParentState ? togglePopover : localToggle;

  const isMobileDevice = isMobile();

  if (ExperienceStatus.PUBLISHED !== experience.status) {
    return null;
  }

  const variant = getVariant(experience);
  const isMultiVariant = getMultiVariant(experience);
  return (
    <ResponsivePopover
      isOpen={isOpen}
      positions={[`bottom`, `top`]}
      onClickOutside={toggle}
      reposition={false}
      containerStyle={{ zIndex: `1000` }}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
          arrowStyle={{ opacity: 1 }}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <PopoverContent
            experience={experience}
            togglePopover={() => toggle(``)}
            setExperience={setExperience}
          />
        </ArrowContainer>
      )}
    >
      <div
        onClick={(ev) => {
          if (!isMobileDevice) {
            ev.stopPropagation();
            toggle(experience.id);
          }
        }}
      >
        {children || (
          <Chip whiteBg={whiteBg}>
            <StaticImage
              style={{ marginRight: `0.5rem` }}
              src="allocation.svg"
              alt="rebalance"
              placeholder="none"
              loading="eager"
              formats={[`png`, `webp`]}
              height={10}
            />
            Allocation:
            {` ${
              isMultiVariant
                ? `multi`
                : `${variant?.publishedChance || variant?.chance || 0}%`
            }`}
          </Chip>
        )}
      </div>
    </ResponsivePopover>
  );
};

export function useToggle() {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return {
    toggle,
    isOpen,
  };
}

function getMultiVariant(experience: Experience) {
  return experience?.variants?.filter((x) => x.name !== CONTROL).length > 1;
}

function getVariant(experience: Experience) {
  return experience?.variants?.find((x) => x.name !== CONTROL);
}
